import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Descriptions, Image } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import startCase from 'lodash/startCase';

import moment from 'moment';
import styles from '../styles';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
  getStoreInward,
  clearStoreInward,
  getQualityCheckView,
  clearQualityCheckView,
} from '../Actions';
import { apiURL } from '../config';

const QualityCheckView = props => {
  const { classes, match } = props;
  const dispatch = useDispatch();

  const storeInward = useSelector(state => state.storeInward);
  const qualityCheckView = useSelector(state => state.qualityCheckView);

  useEffect(() => {
    dispatch(getStoreInward(match.params.storeInwardId));
    dispatch(getQualityCheckView(match.params.id));
    return () => {
      dispatch(clearStoreInward());
      dispatch(clearQualityCheckView());
    };
  }, []);

  const filesColumn = useMemo(
    () => record => {
      if (record.files.length > 0) {
        return record.files.map(file => (
          <Image
            key={file.key}
            src={`${apiURL}/${file.key}`}
            className={classes.jobchartImage}
          />
        ));
      }
    },
    []
  );

  const itemRowKey = useMemo(
    () => record =>
      `N${record.name}S${record.shade}S${record.size}U${record.unitId}`,
    []
  );

  const fabricRowKey = useMemo(
    () => record => `P${record.productId}F${record.fabricId}C${record.colorId}`,
    []
  );

  const accessoryRowKey = useMemo(
    () => record => `P${record.productId}A${record.accessoryId}`,
    []
  );

  const styleRowKey = useMemo(
    () => record =>
      `P${record.productId}S${record.styleId}B${record.baseColor}P${record.printColor}`,
    []
  );

  const itemQCRowKey = useMemo(
    () => record =>
      `N${record.name}S${record.shade}S${record.size}U${record.unitId}Cond${record.condition}`,
    []
  );

  const fabricQCRowKey = useMemo(
    () => record =>
      `P${record.productId}F${record.fabricId}C${record.colorId}F${record.factoryId}R${record.racknoId}Cond${record.condition}`,
    []
  );

  const accessoryQCRowKey = useMemo(
    () => record =>
      `P${record.productId}A${record.accessoryId}F${record.factoryId}R${record.racknoId}Cond${record.condition}`,
    []
  );

  const styleRowQCKey = useMemo(
    () => record =>
      `P${record.productId}S${record.styleId}B${record.baseColorId}P${record.printColorId}`,
    []
  );

  const scroll = useMemo(() => ({ x: 2000 }), []);

  const scrollQC = useMemo(() => ({ x: 1400 }), []);

  const itemsColumns = useMemo(
    () => [
      {
        title: 'Item',
        dataIndex: 'name',
      },
      {
        title: 'Shade',
        dataIndex: 'shade',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
      {
        title: 'Inward Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Actual Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice #',
        dataIndex: 'invoice',
      },
      {
        title: 'Challan #',
        dataIndex: 'challan',
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const fabricsColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
      {
        title: 'Inward Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Actual Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice #',
        dataIndex: 'invoice',
      },
      {
        title: 'Challan #',
        dataIndex: 'challan',
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const accessoryColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
      {
        title: 'Inward Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Actual Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice #',
        dataIndex: 'invoice',
      },
      {
        title: 'Challan #',
        dataIndex: 'challan',
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const stylesColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'style',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
      {
        title: 'Inward Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Actual Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice #',
        dataIndex: 'invoice',
      },
      {
        title: 'Challan #',
        dataIndex: 'challan',
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const itemsQCColumns = useMemo(
    () => [
      {
        title: 'Item',
        dataIndex: 'name',
      },
      {
        title: 'Shade',
        dataIndex: 'shade',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
      },
      {
        title: 'Condition',
        dataIndex: 'condition',
        render: (_text, record) => startCase(record.condition),
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
      },
      {
        title: 'Files',
        render: filesColumn,
      },
    ],
    []
  );

  const fabricsQCColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
      },
      {
        title: 'Factory',
        dataIndex: 'factory',
      },
      {
        title: 'Rack',
        dataIndex: 'rackno',
      },
      {
        title: 'Condition',
        dataIndex: 'condition',
        render: (_text, record) => startCase(record.condition),
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
      },
      {
        title: 'Files',
        render: filesColumn,
      },
    ],
    []
  );

  const accessoryQCColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
      },
      {
        title: 'Factory',
        dataIndex: 'factory',
      },
      {
        title: 'Rack',
        dataIndex: 'rackno',
      },
      {
        title: 'Condition',
        dataIndex: 'condition',
        render: (_text, record) => startCase(record.condition),
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
      },
      {
        title: 'Files',
        render: filesColumn,
      },
    ],
    []
  );

  const stylesQCColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'style',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
      },
      {
        title: 'Condition',
        dataIndex: 'condition',
        render: (_text, record) => startCase(record.condition),
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
      },
      {
        title: 'Files',
        render: filesColumn,
      },
    ],
    []
  );

  return (
    <>
      <Breadcrumbs
        moduleName="Quality Check"
        moduleLink="/qualitycheck/list"
        subModuleName="View"
      />
      <div className={classes.tableStyle}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Customer">
            {qualityCheckView.customer}
          </Descriptions.Item>
          <Descriptions.Item label="BPO #">
            {qualityCheckView.bpoNo}
          </Descriptions.Item>
          <Descriptions.Item label="Process">
            {startCase(qualityCheckView.process)}
          </Descriptions.Item>
          <Descriptions.Item label="Vendor">
            {qualityCheckView.vendor}
          </Descriptions.Item>
          <Descriptions.Item label="RPO #">
            {qualityCheckView.rpoNo}
          </Descriptions.Item>
          <Descriptions.Item label="RPO Date">
            {qualityCheckView.rpoDate
              ? moment(qualityCheckView.rpoDate).format('DD MMM YYYY')
              : null}
          </Descriptions.Item>
          <Descriptions.Item label="Inward #">
            {qualityCheckView.inwardNo}
          </Descriptions.Item>
          <Descriptions.Item label="Inward Date">
            {qualityCheckView.inwardDate
              ? moment(qualityCheckView.inwardDate).format('DD MMM YYYY')
              : null}
          </Descriptions.Item>
          <Descriptions.Item label="Quality Check #">
            {qualityCheckView.qcNo}
          </Descriptions.Item>
          <Descriptions.Item label="Quality Check Date">
            {qualityCheckView.qcDate
              ? moment(qualityCheckView.qcDate).format('DD MMM YYYY')
              : null}
          </Descriptions.Item>
          <Descriptions.Item label="Category">
            {qualityCheckView.isJobOrder ? 'Job order' : 'Purchase order'}
          </Descriptions.Item>
        </Descriptions>
        <br />
        <div className={classes.previewMainHeading}>Store Inward Table</div>
        <br />
        {storeInward.items?.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={itemsColumns}
              dataSource={storeInward.items}
              rowKey={itemRowKey}
              pagination={false}
              scroll={scroll}
              title={() => 'Items Table'}
            />
            <br />
          </>
        ) : null}
        {storeInward.fabrics?.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={fabricsColumns}
              dataSource={storeInward.fabrics}
              rowKey={fabricRowKey}
              pagination={false}
              scroll={scroll}
              title={() => 'Fabrics Table'}
            />
            <br />
          </>
        ) : null}
        {storeInward.accessories?.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={accessoryColumns}
              dataSource={storeInward.accessories}
              rowKey={accessoryRowKey}
              pagination={false}
              scroll={scroll}
              title={() => 'Accessories Table'}
            />
          </>
        ) : null}
        {storeInward.styles?.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={stylesColumns}
              dataSource={storeInward.styles}
              rowKey={styleRowKey}
              pagination={false}
              scroll={scroll}
              title={() => 'Products Table'}
            />
          </>
        ) : null}
        <br />
        <div className={classes.previewMainHeading}>Quality Check Table</div>
        <br />
        {qualityCheckView.items?.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={itemsQCColumns}
              dataSource={qualityCheckView.items}
              rowKey={itemQCRowKey}
              pagination={false}
              scroll={scrollQC}
              title={() => 'Items Table'}
            />
            <br />
          </>
        ) : null}
        {qualityCheckView.fabrics?.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={fabricsQCColumns}
              dataSource={qualityCheckView.fabrics}
              rowKey={fabricQCRowKey}
              pagination={false}
              scroll={scrollQC}
              title={() => 'Fabrics Table'}
            />
            <br />
          </>
        ) : null}
        {qualityCheckView.accessories?.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={accessoryQCColumns}
              dataSource={qualityCheckView.accessories}
              rowKey={accessoryQCRowKey}
              pagination={false}
              scroll={scrollQC}
              title={() => 'Accessories Table'}
            />
          </>
        ) : null}
        {qualityCheckView.styles?.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={stylesQCColumns}
              dataSource={qualityCheckView.styles}
              rowKey={styleRowQCKey}
              pagination={false}
              scroll={scrollQC}
              title={() => 'Products Table'}
            />
          </>
        ) : null}
      </div>
    </>
  );
};

QualityCheckView.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(QualityCheckView);
